var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { bound } from "@frui.ts/helpers";
import { get } from "mobx";
import { hasCondition, hasDefaultValidatorParams } from "./validationRules";
const undefinedValidatorParams = {};
export default class ValidationServiceBase {
    localizationService;
    constructor(localizationService) {
        this.localizationService = localizationService;
    }
    initialize(validators) {
        validators.set("required", this.validateRequired);
        validators.set("requiredIf", this.validateRequiredIf);
        validators.set("number", this.validateNumber);
        validators.set("range", this.validateRange);
        validators.set("equals", this.validateEquals);
        validators.set("equalWith", this.validateEqualWith);
        validators.set("equalIf", this.validateEqualIf);
        validators.set("minLength", this.validateMinLength);
        validators.set("maxLength", this.validateMaxLength);
        validators.set("manualErrors", this.manualValidation);
        validators.set("lowerThan", this.validateLowerThan);
    }
    paramsRegex = /%param(\d+)%/g;
    getLocalizedMessage(messageTemplate, translationCode, params, model = "") {
        let message = messageTemplate ?? this.localizationService.translateGeneral(translationCode);
        if (params !== undefined) {
            if (message.includes("%params%")) {
                message = message.replace("%params%", params);
            }
            else if (Array.isArray(params)) {
                message = message.replace(this.paramsRegex, (match, number) => {
                    const paramValue = params[number - 1];
                    return paramValue ? this.localizationService.translateAttribute(model, paramValue) : match;
                });
            }
        }
        return message;
    }
    getValidationMessage(isValid, validatorParams, translationCode, messageParams) {
        if (isValid) {
            return undefined;
        }
        const { message, translationCode: translation } = hasDefaultValidatorParams(validatorParams)
            ? validatorParams
            : undefinedValidatorParams;
        return this.getLocalizedMessage(message, translation ?? translationCode, messageParams);
    }
    validateRequired(value, propertyName, entity, params) {
        if (!params) {
            return undefined;
        }
        const hasError = value == null || value === "" || value.length === 0;
        return this.getValidationMessage(!hasError, params, "validators.required");
    }
    validateRequiredIf(value, propertyName, entity, params) {
        if (!hasCondition(params) || !params.condition(entity)) {
            return undefined;
        }
        const hasError = value == null || value === "" || value.length === 0;
        return this.getValidationMessage(!hasError, params, "validators.requiredIf");
    }
    validateLowerThan(value, propertyName, entity, params) {
        if (value == null || value === "") {
            return undefined;
        }
        const currentValue = parseInt(value, 10);
        const compareValue = parseInt(entity[params.key], 10);
        if (currentValue <= compareValue) {
            return undefined;
        }
        return this.getLocalizedMessage(params.message, params.translationCode ?? "validators.lowerThan", params.key.toString(), params.modelName);
    }
    validateNumber(value, propertyName, entity, params) {
        if (value == null || value === "") {
            return undefined;
        }
        const isValid = typeof value === "number" || value === parseInt(value, 10).toString();
        return this.getValidationMessage(isValid, params, "validators.number");
    }
    validateRange(value, propertyName, entity, params) {
        if (value == null || value === "") {
            return undefined;
        }
        const number = typeof value === "number" ? value : parseFloat(value);
        if (params.min !== undefined && params.min > number) {
            return this.getValidationMessage(false, params, "validators.min", params.min);
        }
        if (params.minExclusive !== undefined && params.minExclusive >= number) {
            return this.getValidationMessage(false, params, "validators.minExclusive", params.minExclusive);
        }
        if (params.max !== undefined && params.max < number) {
            return this.getValidationMessage(false, params, "validators.max", params.max);
        }
        if (params.maxExclusive !== undefined && params.maxExclusive <= number) {
            return this.getValidationMessage(false, params, "validators.maxExclusive", params.maxExclusive);
        }
        return undefined;
    }
    validateEquals(value, propertyName, entity, params) {
        const expectedValue = params.parameter ?? params;
        const isValid = value === expectedValue;
        return this.getValidationMessage(isValid, params, "validators.equals", [propertyName, expectedValue]);
    }
    validateEqualWith(value, propertyName, entity, params) {
        const otherPropertyName = typeof params === "string" ? params : params.parameter;
        const isValid = value === entity[otherPropertyName];
        return this.getValidationMessage(isValid, params, "validators.equalWith", [propertyName, otherPropertyName]);
    }
    validateEqualIf(value, propertyName, entity, params) {
        if (!hasCondition(params) || !params.condition(entity)) {
            return undefined;
        }
        const expectedValue = params.parameter ?? params;
        const isValid = value === expectedValue;
        return this.getValidationMessage(isValid, params, "validators.equalIf", [propertyName, expectedValue]);
    }
    validateMinLength(value, propertyName, entity, params) {
        const minLength = typeof params === "number" ? params : params.parameter;
        const hasError = value !== null && value !== undefined && value !== "" && value.toString().length < minLength;
        return this.getValidationMessage(!hasError, params, "validators.minLength", [propertyName, minLength]);
    }
    validateMaxLength(value, propertyName, entity, params) {
        const maxLength = typeof params === "number" ? params : params.parameter;
        const hasError = value !== null && value !== undefined && value !== "" && value.length > maxLength;
        return this.getValidationMessage(!hasError, params, "validators.maxLength", [propertyName, maxLength]);
    }
    manualValidation(value, propertyName, entity, params) {
        return get(params, propertyName);
    }
}
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, Object, Object]),
    __metadata("design:returntype", void 0)
], ValidationServiceBase.prototype, "validateRequired", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, Object, Object]),
    __metadata("design:returntype", void 0)
], ValidationServiceBase.prototype, "validateRequiredIf", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, Object, Object]),
    __metadata("design:returntype", void 0)
], ValidationServiceBase.prototype, "validateLowerThan", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, Object, Object]),
    __metadata("design:returntype", void 0)
], ValidationServiceBase.prototype, "validateNumber", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, Object, Object]),
    __metadata("design:returntype", void 0)
], ValidationServiceBase.prototype, "validateRange", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, Object, Object]),
    __metadata("design:returntype", void 0)
], ValidationServiceBase.prototype, "validateEquals", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, Object, Object]),
    __metadata("design:returntype", void 0)
], ValidationServiceBase.prototype, "validateEqualWith", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, Object, Object]),
    __metadata("design:returntype", void 0)
], ValidationServiceBase.prototype, "validateEqualIf", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, Object, Object]),
    __metadata("design:returntype", void 0)
], ValidationServiceBase.prototype, "validateMinLength", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String, Object, Object]),
    __metadata("design:returntype", void 0)
], ValidationServiceBase.prototype, "validateMaxLength", null);
//# sourceMappingURL=validationServiceBase.js.map